import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType, AccountInfo } from '@azure/msal-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { b2cPolicies } from '../config/auth-config';
import { NGXLogger } from 'ngx-logger';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private router = inject(Router);
  private logger = inject(NGXLogger);
  private readonly destroying$ = new Subject<void>();

  // Authentication state
  isInitialized = signal(false);
  isAuthenticated = signal(false);
  currentUser = signal<AccountInfo | null>(null);
  isLoggingOut = signal(false);

  constructor() {
    this.initializeAuth();
  }

  /**
   * Initialize authentication state and handle MSAL events
   */
  private async initializeAuth(): Promise<void> {
    try {
      // Ensure MSAL instance is fully initialized before proceeding
      await this.msalService.instance.initialize();
      this.logger.debug('MSAL instance initialized successfully');

      // Handle redirect and initialize MSAL
      this.msalService.handleRedirectObservable().subscribe({
        next: (result) => {
          this.logger.debug('MSAL initialization complete');

          if (result) {
            this.logger.debug('Login successful:', result);
            this.setActiveAccount(result.account);
            this.updateAuthState();

            // Only navigate after successful login redirect
            this.navigateBasedOnAuth();
          } else {
            // No redirect result, just update state
            this.updateAuthState();
          }

          this.isInitialized.set(true);

          // Reset logout state on successful initialization
          this.isLoggingOut.set(false);
        },
        error: (error) => {
          this.logger.error('MSAL initialization error:', error);
          this.isInitialized.set(true);
          // Reset logout state on error
          this.isLoggingOut.set(false);
        },
      });

      // Listen for MSAL events only after initialization
      this.msalBroadcastService.msalSubject$
        .pipe(
          filter(
            (msg: EventMessage) =>
              msg.eventType === EventType.LOGIN_SUCCESS ||
              msg.eventType === EventType.LOGOUT_START ||
              msg.eventType === EventType.LOGOUT_SUCCESS ||
              msg.eventType === EventType.LOGOUT_FAILURE ||
              msg.eventType === EventType.LOGOUT_END,
          ),
          takeUntil(this.destroying$),
        )
        .subscribe((result) => {
          this.logger.debug('MSAL Event:', result.eventType, result);

          if (result.eventType === EventType.LOGIN_SUCCESS) {
            if (
              result.payload &&
              'account' in result.payload &&
              result.payload.account
            ) {
              this.setActiveAccount(result.payload.account);
              this.updateAuthState();

              // Reset logout state on successful login
              this.isLoggingOut.set(false);

              // Don't auto-navigate here - let the auth guard handle routing
              this.logger.debug(
                'Login success event processed, letting router handle navigation',
              );
            }
          } else if (result.eventType === EventType.LOGOUT_START) {
            this.logger.debug('Logout process started - setting logout state');
            this.isLoggingOut.set(true);
            // Clear local authentication state immediately to prevent inconsistent UI state
            this.clearLocalAuthState();
          } else if (
            result.eventType === EventType.LOGOUT_SUCCESS ||
            result.eventType === EventType.LOGOUT_END
          ) {
            this.logger.debug('Logout completed successfully');
            this.isLoggingOut.set(false);
            this.router.navigate(['/login']);
          } else if (result.eventType === EventType.LOGOUT_FAILURE) {
            this.logger.debug('Logout failed - resetting logout state');
            this.isLoggingOut.set(false);
          }
        });
    } catch (error) {
      this.logger.error('Failed to initialize MSAL instance:', error);
      this.isInitialized.set(true);
      this.isLoggingOut.set(false);
    }
  }

  /**
   * Set the active account in MSAL
   */
  private setActiveAccount(account: AccountInfo | null): void {
    if (account) {
      this.logger.debug('Setting active account:', account);
      this.msalService.instance.setActiveAccount(account);
    }
  }

  /**
   * Update authentication state signals
   */
  private updateAuthState(): void {
    const account = this.msalService.instance.getActiveAccount();
    this.currentUser.set(account);
    this.isAuthenticated.set(!!account);
  }

  /**
   * Check if user is authenticated
   */
  isUserAuthenticated(): boolean {
    // If MSAL is not initialized yet, consider user as not authenticated
    if (!this.isInitialized()) {
      return false;
    }

    // If user is in logout process, consider them as not authenticated
    if (this.isLoggingOut()) {
      return false;
    }

    try {
      return !!this.msalService.instance.getActiveAccount();
    } catch (error) {
      this.logger.warn('Error checking authentication status:', error);
      return false;
    }
  }

  /**
   * Check if user is currently logging out
   */
  isUserLoggingOut(): boolean {
    return this.isLoggingOut();
  }

  /**
   * Navigate to appropriate route based on authentication status
   */
  navigateBasedOnAuth(): void {
    const currentUrl = this.router.url;
    this.logger.debug('Current URL:', currentUrl);

    if (this.isUserAuthenticated()) {
      this.logger.debug('User authenticated');

      // Only redirect to dashboard if user is on login page or root
      // Don't interfere with navigation to other protected routes
      if (currentUrl === '/login' || currentUrl === '/') {
        this.logger.debug(
          'Redirecting authenticated user from login/root to dashboard',
        );
        this.router.navigate(['/dashboard']);
      } else {
        this.logger.debug('User is on a valid route, not redirecting');
        // User is already on a valid route, don't redirect
      }
    } else {
      this.logger.debug('User not authenticated');
      // Let the auth guard handle redirects to login for protected routes
      if (currentUrl === '/') {
        // Only redirect from root to trigger the auth guard
        this.router.navigate(['/dashboard']); // This will trigger the guard
      }
    }
  }

  /**
   * Login with B2C Sign Up/Sign In policy
   */
  login(): void {
    this.msalService.loginRedirect({
      scopes: b2cPolicies.scopes,
    });
  }

  /**
   * Logout user
   */
  logout(): void {
    this.logger.debug(
      'Initiating logout - MSAL will handle state management via events',
    );

    // Let MSAL handle the logout process and state management via events
    // LOGOUT_START event will trigger state clearing automatically
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: window.location.origin + '/login',
    });
  }

  /**
   * Clear local authentication state
   */
  private clearLocalAuthState(): void {
    this.logger.debug('Clearing local authentication state');

    // Clear MSAL active account
    this.msalService.instance.setActiveAccount(null);

    // Update local signals
    this.currentUser.set(null);
    this.isAuthenticated.set(false);
  }

  /**
   * Cleanup when service is destroyed
   */
  destroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }
}
